import { ReactNode } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardProps,
  Stack,
  StackProps,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Action } from '../api/gql';
import { ActionIcon } from './invoice/ScheduledActionIcon';

type PlanItemProps = StackProps & {
  icon: ReactNode;
  title: string;
  subtitle: string;
  disabled?: boolean;
};
const PlanItem = ({ icon, title, subtitle, disabled, ...rest }: PlanItemProps) => {
  return (
    <Stack direction={'row'} spacing={2} {...rest}>
      <Avatar sx={{ bgcolor: disabled ? '' : 'primary.main' }} variant={'rounded'}>
        {icon}
      </Avatar>
      <Stack>
        <Typography color={disabled ? 'text.disabled' : undefined} fontWeight={'bold'} fontSize={'medium'}>
          {title}
        </Typography>
        <Typography color={disabled ? 'text.disabled' : 'secondary.300'} variant={'body2'}>
          {subtitle}
        </Typography>
      </Stack>
    </Stack>
  );
};

const RecommendedFlag = () => {
  const { t } = useTranslation('billing');
  return (
    <Box
      sx={(theme) => ({
        position: 'absolute',
        top: 0,
        left: '50%',
        translate: '-50% 0%',
        bgcolor: 'primary.main',
        py: 1,
        px: 2,
        overflow: 'hidden',
        borderBottomLeftRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
        color: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      })}
    >
      <Typography variant={'body2'} fontWeight={'bold'}>
        {t('recommended')}
      </Typography>
    </Box>
  );
};

type PlanCardProps = CardProps & {
  title: string;
  subtitle: string;
  plansHeading: string;
  noEmails: number | string;
  emailsDescription: string;
  disableEmail?: boolean;
  noSms: number | string;
  smsDescription: string;
  disableSms?: boolean;
  noCalls: number | string;
  callsDescription: string;
  disableCalls?: boolean;
  endOfRemindingDescription: string;
  priceForUnit: string;
  recommended?: boolean;
  content?: ReactNode;
  actionContent?: ReactNode;
};

export const PlanCard = ({
  title,
  subtitle,
  plansHeading,
  noEmails,
  emailsDescription,
  disableEmail,
  noCalls,
  callsDescription,
  disableCalls,
  noSms,
  smsDescription,
  disableSms,
  endOfRemindingDescription,
  priceForUnit,
  recommended,
  content,
  actionContent,
}: PlanCardProps) => {
  const { t } = useTranslation();
  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 1,
        ...(recommended
          ? {
              borderColor: 'primary.main',
              borderWidth: 2,
              position: 'relative',
              overflow: 'visible',
              py: 4,
            }
          : null),
      }}
    >
      {recommended ? <RecommendedFlag /> : null}
      <CardHeader
        title={title}
        subheader={subtitle}
        sx={{
          whiteSpace: 'break-spaces',
          minHeight: { md: 160 },
          alignItems: 'flex-start',
        }}
      />
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant={'subtitle2'}
          component={'span'}
          textTransform={'uppercase'}
          letterSpacing={2}
          color={'text.secondary'}
        >
          {plansHeading}
        </Typography>
        <Stack spacing={{ md: 4, xs: 2 }} pt={{ md: 3, xs: 2 }} flex={1} minHeight={{ md: 275 }}>
          <PlanItem
            icon={<ActionIcon action={Action.Email} />}
            title={`${noEmails}\u00D7 ${t('email')}`}
            subtitle={emailsDescription}
            disabled={!noEmails || disableEmail}
          />
          <PlanItem
            icon={<ActionIcon action={Action.Sms} />}
            title={`${noSms}\u00D7 ${t('sms')}`}
            subtitle={smsDescription}
            disabled={!noSms || disableSms}
          />
          <PlanItem
            disabled={!noCalls || disableCalls}
            icon={<ActionIcon action={Action.Call} />}
            title={`${noCalls}\u00D7 ${t('phone')}`}
            subtitle={callsDescription ?? ''}
          />
        </Stack>
        <Stack flex={1} spacing={2} mt={2}>
          <Stack spacing={1} sx={{ color: !noCalls ? 'text.disabled' : 'inherit' }} minHeight={70}>
            <Typography fontWeight={'bold'}>{t('plan.endOfReminding', { ns: 'billing' })}</Typography>
            <Typography color={!noCalls ? 'text.disabled' : 'text.secondary'}>{endOfRemindingDescription}</Typography>
          </Stack>
          <Stack spacing={1}>
            <Typography fontWeight={'bold'}>{t('plan.priceForRemindingOneInvoice', { ns: 'billing' })}</Typography>
            <Typography color={'text.secondary'}>{priceForUnit}</Typography>
          </Stack>
          {content}
        </Stack>
      </CardContent>
      <CardActions>{actionContent}</CardActions>
    </Card>
  );
};
